import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';

/**
 * Knobs the schedule-page operator turns to make the gantt-preview reflect a
 * realistic execution profile, since the backend's `next` calculation only
 * predicts WHEN events fire, not how long they'll take. None of these knobs
 * persist — they feed only the local gantt rendering. The "real" runtime
 * profile lives with the task definition + runner host (TASK-165 input
 * schemas + executionPolicy.timeoutSeconds).
 */
export interface SimulationParams {
    /** Mean execution duration in milliseconds. 0 = no override (point markers). */
    durationMs: number;
    /** ± percentage to jitter the duration (0–100). Negative jitter clamped to 1ms. */
    jitterPercent: number;
    /** % of executions painted "failed" (red). 0–100. */
    failurePercent: number;
    /** Constant offset added to every startTime, in milliseconds.
     *  Positive = late start; negative = early. */
    driftMs: number;
    /** How many upcoming events to fetch from the calculator for the gantt
     *  preview. Higher values give a longer-range view but cost more compute
     *  per refresh (each event is a calculator iteration). 1–500. */
    sampleSize: number;
}

@Component({
    selector: 'simulation-options',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatOption,
        MatButtonToggleModule,
        MatSlideToggleModule,
        MatTooltipModule,
        MatIconModule,
    ],
    templateUrl: './simulation-options.component.html',
    styleUrl: './simulation-options.component.css',
})
export class SimulationOptionsComponent implements OnInit {

    /** Fired when the user-applied values change. Default behaviour (reactive
     *  mode): fires on every input. Dialog mode (reactive=false): fires only
     *  when commit() is called by the parent (e.g. on the dialog's Apply
     *  button). Cancel discards by simply not calling commit(). */
    @Output() paramsChange = new EventEmitter<SimulationParams>();

    /** When false, suppresses the live valueChanges → emit pipeline; the
     *  parent must call commit() to publish the form's current values.
     *  Used by the preview-options dialog so cancel can discard pending
     *  edits cleanly. Defaults to true (the inline / always-bound case). */
    @Input() reactive: boolean = true;

    /** Initial values; allow the parent to seed them from a saved-as-default
     *  if/when we hook persistence. */
    @Input() initial: SimulationParams = {
        durationMs: 60_000,        // 1 minute — realistic for "did it run?" type tasks
        jitterPercent: 0,
        failurePercent: 0,
        driftMs: 0,
        sampleSize: 10,            // matches the previously-hardcoded value
    };

    enabled       = new FormControl<boolean>(true);
    /** User enters duration in their chosen unit; durationMs is reconstructed. */
    durationValue = new FormControl<number>(1, [Validators.min(0)]);
    /** seconds | minutes | hours — multiplier for durationValue. */
    durationUnit  = new FormControl<'seconds' | 'minutes' | 'hours'>('minutes');
    jitterPercent = new FormControl<number>(0, [Validators.min(0), Validators.max(100)]);
    failurePercent= new FormControl<number>(0, [Validators.min(0), Validators.max(100)]);
    /** Same value+unit pattern as duration. */
    driftValue    = new FormControl<number>(0);
    driftUnit     = new FormControl<'seconds' | 'minutes' | 'hours'>('seconds');
    /** Number of upcoming events the calculator returns. 500 max so a runaway
     *  pattern (every minute, no gates) doesn't blow up the gantt render. */
    sampleSize    = new FormControl<number>(10, [Validators.min(1), Validators.max(500)]);

    form = new FormGroup({
        enabled:        this.enabled,
        durationValue:  this.durationValue,
        durationUnit:   this.durationUnit,
        jitterPercent:  this.jitterPercent,
        failurePercent: this.failurePercent,
        driftValue:     this.driftValue,
        driftUnit:      this.driftUnit,
        sampleSize:     this.sampleSize,
    });

    ngOnInit(): void {
        // Seed from @Input initial — split durationMs back into value+unit.
        const seed = this.splitDuration(this.initial.durationMs);
        this.durationValue.setValue(seed.value, { emitEvent: false });
        this.durationUnit.setValue(seed.unit, { emitEvent: false });
        this.jitterPercent.setValue(this.initial.jitterPercent, { emitEvent: false });
        this.failurePercent.setValue(this.initial.failurePercent, { emitEvent: false });
        const driftSeed = this.splitDuration(Math.abs(this.initial.driftMs), this.initial.driftMs < 0 ? -1 : 1);
        this.driftValue.setValue(driftSeed.value * driftSeed.sign, { emitEvent: false });
        this.driftUnit.setValue(driftSeed.unit, { emitEvent: false });
        this.sampleSize.setValue(this.initial.sampleSize ?? 10, { emitEvent: false });

        if (this.reactive) {
            // Reactive mode (the default for non-dialog uses): emit on every
            // valueChanges, plus an initial emit so the parent picks up the
            // seeded values without a user touch.
            setTimeout(() => this.form.valueChanges.subscribe(() => this.emit()), 0);
            setTimeout(() => this.emit(), 0);
        }
        // Non-reactive (dialog) mode: no auto-emit. Parent calls commit()
        // explicitly when the user clicks Apply; Cancel just closes the
        // dialog without committing, so the seeded values are discarded.
    }

    /** Public hook for non-reactive callers (e.g. the preview-options dialog's
     *  Apply button). Computes the current SimulationParams from the form
     *  and emits paramsChange exactly once. */
    commit(): void {
        this.emit();
    }

    private emit(): void {
        // Sample size is independent of the simulation toggle — operators may
        // want a long-range gantt even with no duration override.
        const sample = this.clamp(this.sampleSize.value ?? 10, 1, 500);
        const out: SimulationParams = this.enabled.value === false
                ? { durationMs: 0, jitterPercent: 0, failurePercent: 0, driftMs: 0, sampleSize: sample }
                : {
                    durationMs: this.toMs(this.durationValue.value ?? 0, this.durationUnit.value ?? 'seconds'),
                    jitterPercent: this.clamp(this.jitterPercent.value ?? 0, 0, 100),
                    failurePercent: this.clamp(this.failurePercent.value ?? 0, 0, 100),
                    driftMs: this.toMs(this.driftValue.value ?? 0, this.driftUnit.value ?? 'seconds'),
                    sampleSize: sample,
                };
        this.paramsChange.emit(out);
    }

    private toMs(value: number, unit: 'seconds' | 'minutes' | 'hours'): number {
        const mult = unit === 'hours' ? 3_600_000 : unit === 'minutes' ? 60_000 : 1_000;
        return Math.round((value || 0) * mult);
    }

    /** Reverse of toMs — pick the largest unit that produces an integer-ish
     *  value so a 60_000 ms seed comes back as "1 minute" not "60 seconds". */
    private splitDuration(ms: number, sign: number = 1): { value: number; unit: 'seconds' | 'minutes' | 'hours'; sign: number } {
        if (ms === 0) return { value: 0, unit: 'seconds', sign };
        if (ms % 3_600_000 === 0) return { value: ms / 3_600_000, unit: 'hours', sign };
        if (ms % 60_000 === 0) return { value: ms / 60_000, unit: 'minutes', sign };
        return { value: ms / 1_000, unit: 'seconds', sign };
    }

    private clamp(v: number, lo: number, hi: number): number {
        return Math.max(lo, Math.min(hi, v));
    }

    /** Reset to "no simulation". Also emits so the gantt clears. Sample size
     *  resets to 10 (the documented default) so a previous bumped value
     *  doesn't quietly persist after a reset. */
    reset(): void {
        this.enabled.setValue(true, { emitEvent: false });
        this.durationValue.setValue(0, { emitEvent: false });
        this.durationUnit.setValue('seconds', { emitEvent: false });
        this.jitterPercent.setValue(0, { emitEvent: false });
        this.failurePercent.setValue(0, { emitEvent: false });
        this.driftValue.setValue(0, { emitEvent: false });
        this.driftUnit.setValue('seconds', { emitEvent: false });
        this.sampleSize.setValue(10, { emitEvent: false });
        this.emit();
    }
}
